<template>
  <Modal
    :show="modalVisible"
    action-text="Translate"
    :disabled="actionDisabled"
    :disable-close="submitting"
    width="large"
    :button-props="{ 'data-testid': 'translate-job-confirm' }"
    @action="confirm"
    @close="close"
  >
    <template #title>
      {{ isTemplate ? 'Translate template' : 'Translate job post' }}
    </template>

    <template #content>
      <div data-testid="translate-job-modal">
        <p>
          A new draft will be created with the title and content translated by AI.
          The original stays unchanged.
        </p>
        <p class="mt-4">
          Company and location are not translated.
        </p>
        <p
          v-if="!isTemplate"
          class="mt-4"
        >
          Locked header and footer content stays as it is.
        </p>
        <p class="mt-4">
          Review the translation before publishing.
        </p>
        <p
          class="mt-4"
          data-testid="translate-job-detected-language"
        >
          Detected language: {{ detectedLabel }}
        </p>
        <v-select
          v-if="needsLanguageChoice"
          v-model="targetLanguage"
          class="mt-4"
          data-testid="translate-job-language"
          :items="languageItems"
          item-title="title"
          item-value="value"
          label="Translate to"
          placeholder="Select a language"
          variant="outlined"
          density="compact"
          hide-details
          :disabled="submitting || isRevisionSaving"
          :menu-props="{ contentClass: 'translate-job-language-menu' }"
        />
        <p
          v-else-if="fixedTargetLabel"
          class="mt-4"
          data-testid="translate-job-fixed-target"
        >
          Will be translated to: {{ fixedTargetLabel }}
        </p>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { translateJob } from '@/api/dfd';
import Modal from '@/components/shared/Modal.vue';
import {
  TRANSLATION_LANGUAGES,
  translationLanguageLabel,
  translationTargets,
} from '@/helpers/language';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useTranslateJob } from '@/stores/document/translateJob';
import { useEditor } from '@/stores/editor/editor';

const GENERIC_ERROR = 'Translation failed, please try again.';

const router = useRouter();
const { setLoading } = useAppLoading();
const { addErrorSnackbar } = useAppSnackbar();
const translateStore = useTranslateJob();
const { modalVisible } = storeToRefs(translateStore);
const {
  languageCode,
  isTemplate,
  jobId,
  isRevisionSaving,
  revisionId,
} = storeToRefs(useEditor());

const targetLanguage = ref(null);
const submitting = ref(false);

const detectedLabel = computed(() => translationLanguageLabel(languageCode.value));
const targets = computed(() => translationTargets(languageCode.value));
const needsLanguageChoice = computed(() => targets.value.length > 1);
const fixedTarget = computed(() => (targets.value.length === 1 ? targets.value[0] : null));
const fixedTargetLabel = computed(() => (
  fixedTarget.value ? TRANSLATION_LANGUAGES[fixedTarget.value] : ''
));
const languageItems = computed(() => targets.value.map((code) => ({
  title: TRANSLATION_LANGUAGES[code],
  value: code,
})));
const resolvedTarget = computed(() => (
  needsLanguageChoice.value ? targetLanguage.value : fixedTarget.value
));
const actionDisabled = computed(() => (
  submitting.value || isRevisionSaving.value || !resolvedTarget.value
));

watch(modalVisible, (visible) => {
  if (visible) {
    targetLanguage.value = null;
    submitting.value = false;
  }
}, { immediate: true });

const close = () => {
  if (submitting.value) return;
  translateStore.setModalVisible(false);
};

const confirm = async () => {
  if (submitting.value || actionDisabled.value) return;

  submitting.value = true;
  setLoading({ modalLoading: true });

  try {
    const { id } = await translateJob({
      id: jobId.value,
      targetLanguage: resolvedTarget.value,
    });

    translateStore.setModalVisible(false);
    await router.push({
      name: 'editor-view-reload',
      params: { id },
      query: revisionId.value ? { sideRevision: revisionId.value, translatedDraft: 'true' } : {},
    });
  } catch (error) {
    addErrorSnackbar({
      text: error?.message || GENERIC_ERROR,
      timeout: 5000,
    });
  } finally {
    submitting.value = false;
    setLoading({ modalLoading: false });
  }
};
</script>
